import { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Typography,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { notify } from '../../../common/functions/react-toastify.function';

const HUAWEI_VENDOR = 'Huawei Technologies Pakistan (Pvt.) Ltd';

/** Narrow label for the vendor control; full name in menu + tooltip */
const VENDOR_LABEL = 'Vendor';

const SYSTEM_PO_TYPE_OPTIONS = ['FTTS', 'OTN', 'IPRAN'];

const newRow = () => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  po: '',
  po_date: '',
  vendor: HUAWEI_VENDOR,
  total_amount: '',
  po_delivery_date: '',
  type: '',
});

const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    bgcolor: '#fff',
  },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
};

const selectFormSx = {
  width: 168,
  minWidth: 168,
  maxWidth: 168,
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    bgcolor: '#fff',
  },
  '& .MuiSelect-select': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '0.8125rem',
    py: 1,
  },
};

const typeSelectFormSx = {
  ...selectFormSx,
  width: 120,
  minWidth: 120,
  maxWidth: 120,
};

export default function CreateSystemPoModal({ open, onClose, onSave, isSaving }) {
  const [rows, setRows] = useState([newRow()]);

  const rowErrors = useMemo(
    () =>
      rows.map((r) => {
        const e = {};
        if (!String(r.po || '').trim()) {
          e.po = 'Required';
        }
        if (!String(r.total_amount || '').trim()) {
          e.total_amount = 'Required';
        }
        if (!String(r.type || '').trim()) {
          e.type = 'Required';
        }
        return e;
      }),
    [rows]
  );

  const hasAnyError = rowErrors.some((e) => Object.keys(e).length > 0);

  useEffect(() => {
    if (!open) {
      setRows([newRow()]);
    }
  }, [open]);

  const updateRow = useCallback((index, patch) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }, []);

  const handleFieldChange = useCallback(
    (index, key) => (e) => {
      const value = e.target.value;
      updateRow(index, { [key]: value });
    },
    [updateRow]
  );

  const handleAddRow = useCallback(() => {
    setRows((prev) => {
      const next = [...prev];
      next.push(newRow());
      return next;
    });
  }, []);

  const handleRemoveRow = useCallback((index) => {
    setRows((prev) => {
      if (prev.length <= 1) {
        return prev;
      }
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const handleSubmit = async () => {
    if (hasAnyError) {
      notify('error', 'Please fill required fields on all rows');
      return;
    }
    if (typeof onSave === 'function') {
      const payload = rows.map((row) => {
        const copy = { ...row };
        delete copy.id;
        return { ...copy, vendor: HUAWEI_VENDOR };
      });
      await onSave(payload);
    }
  };

  const gridTemplate =
    'minmax(100px,1.05fr) minmax(120px,1.05fr) 120px 168px ' +
    'minmax(118px,1fr) minmax(118px,1fr) max-content';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: '16px',
          border: '1px solid #e4e7ec',
          boxShadow: '0 12px 40px rgba(15, 23, 42, 0.12)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          py: 2,
          px: 2.5,
          borderBottom: '1px solid #f2f4f7',
          bgcolor: '#fafafa',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box>
            <Typography component="span" variant="h6" sx={{ fontWeight: 700, color: '#101828' }}>
              Create purchase orders
            </Typography>
            <Typography variant="body2" sx={{ color: '#667085', mt: 0.5, display: 'block' }}>
              One line per PO. Use the green + at the end of the last row to add another.
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            disabled={isSaving}
            size="small"
            aria-label="Close"
            sx={{
              color: '#667085',
              bgcolor: '#fff',
              border: '1px solid #e4e7ec',
              '&:hover': { bgcolor: '#f9fafb' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, pt: 2.5, pb: 1, bgcolor: '#f9fafb' }}>
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: '12px',
              border: '1px solid #e4e7ec',
              overflow: 'hidden',
              bgcolor: '#fff',
              minWidth: 640,
            }}
          >
            {rows.map((row, index) => {
              const isLast = index === rows.length - 1;
              const err = rowErrors[index] || {};
              const showRowActions = isLast || index > 0;

              return (
                <Box
                  key={row.id}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: gridTemplate,
                    columnGap: 1,
                    rowGap: 0.5,
                    px: 2,
                    py: 1.5,
                    alignItems: 'flex-start',
                    borderBottom: index < rows.length - 1 ? '1px solid #f2f4f7' : 'none',
                    '&:hover': { bgcolor: '#fcfcfd' },
                  }}
                >
                  <TextField
                    label="PO number"
                    value={row.po}
                    onChange={handleFieldChange(index, 'po')}
                    required
                    error={!!err.po}
                    helperText={err.po || ''}
                    size="small"
                    fullWidth
                    sx={textFieldSx}
                  />

                  <TextField
                    label="Total amount"
                    type="number"
                    value={row.total_amount}
                    onChange={handleFieldChange(index, 'total_amount')}
                    inputProps={{ min: 0, step: '1', inputMode: 'numeric' }}
                    onKeyDown={(e) => {
                      // Prevent non-numeric exponent / sign chars in number inputs
                      if (['e', 'E', '+', '-'].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    required
                    error={!!err.total_amount}
                    helperText={err.total_amount || ''}
                    size="small"
                    fullWidth
                    sx={textFieldSx}
                  />

                  <FormControl size="small" required error={!!err.type} sx={typeSelectFormSx}>
                    <InputLabel id={`type-label-${row.id}`} shrink>
                      Type
                    </InputLabel>
                    <Select
                      labelId={`type-label-${row.id}`}
                      label="Type"
                      notched
                      value={row.type || ''}
                      displayEmpty
                      renderValue={(selected) => {
                        if (!selected) {
                          return (
                            <span style={{ color: '#98a2b3', fontStyle: 'normal' }}>
                              Select type
                            </span>
                          );
                        }
                        return selected;
                      }}
                      onChange={handleFieldChange(index, 'type')}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            borderRadius: '10px',
                            mt: 0.5,
                            boxShadow: '0 8px 24px rgba(15,23,42,0.12)',
                          },
                        },
                      }}
                    >
                      {SYSTEM_PO_TYPE_OPTIONS.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                    {err.type ? (
                      <Typography
                        component="span"
                        sx={{
                          color: '#d32f2f',
                          fontSize: '0.75rem',
                          mt: 0.5,
                          mx: '14px',
                          display: 'block',
                        }}
                      >
                        {err.type}
                      </Typography>
                    ) : null}
                  </FormControl>

                  <FormControl size="small" required sx={selectFormSx}>
                    <InputLabel id={`vendor-label-${row.id}`} shrink>
                      {VENDOR_LABEL}
                    </InputLabel>
                    <Select
                      labelId={`vendor-label-${row.id}`}
                      label={VENDOR_LABEL}
                      notched
                      value={row.vendor || HUAWEI_VENDOR}
                      displayEmpty
                      title={HUAWEI_VENDOR}
                      onChange={(e) => updateRow(index, { vendor: e.target.value })}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxWidth: 360,
                            borderRadius: '10px',
                            mt: 0.5,
                            boxShadow: '0 8px 24px rgba(15,23,42,0.12)',
                          },
                        },
                      }}
                    >
                      <MenuItem value={HUAWEI_VENDOR} sx={{ whiteSpace: 'normal', py: 1 }}>
                        {HUAWEI_VENDOR}
                      </MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    label="PO date"
                    type="date"
                    value={row.po_date}
                    onChange={handleFieldChange(index, 'po_date')}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    fullWidth
                    sx={textFieldSx}
                  />

                  <TextField
                    label="Delivery date"
                    type="date"
                    value={row.po_delivery_date}
                    onChange={handleFieldChange(index, 'po_delivery_date')}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    fullWidth
                    sx={{
                      ...textFieldSx,
                      ...(showRowActions ? null : { gridColumn: '6 / 8' }),
                    }}
                  />

                  {showRowActions && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        justifySelf: 'end',
                        alignItems: 'flex-start',
                        gap: 1,
                        pt: 0.75,
                        flexWrap: 'nowrap',
                      }}
                    >
                      {isLast && (
                        <Tooltip title="Add another PO">
                          <IconButton
                            onClick={handleAddRow}
                            disabled={isSaving}
                            aria-label="Add PO row"
                            size="small"
                            sx={{
                              color: '#fff',
                              bgcolor: '#16a34a',
                              width: 36,
                              height: 36,
                              '&:hover': { bgcolor: '#15803d' },
                              '&.Mui-disabled': { bgcolor: '#86efac', color: '#fff' },
                            }}
                          >
                            <AddIcon sx={{ fontSize: 22 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {index > 0 && (
                        <Tooltip title="Remove this row">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveRow(index)}
                              disabled={isSaving}
                              aria-label="Remove PO row"
                              sx={{
                                color: '#fff',
                                bgcolor: '#dc2626',
                                width: 36,
                                height: 36,
                                '&:hover': { bgcolor: '#b91c1c' },
                                '&.Mui-disabled': { bgcolor: '#fca5a5', color: '#fff' },
                              }}
                            >
                              <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Paper>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 2,
          bgcolor: '#fff',
          borderTop: '1px solid #f2f4f7',
          gap: 1,
        }}
      >
        <Button
          onClick={onClose}
          disabled={isSaving}
          variant="outlined"
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            borderColor: '#d0d5dd',
            color: '#344054',
            px: 2,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isSaving || hasAnyError}
          variant="contained"
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            px: 2.5,
            backgroundColor: '#bd1d23',
            boxShadow: 'none',
            '&:hover': { backgroundColor: '#a0181e', boxShadow: 'none' },
          }}
        >
          {isSaving ? 'Saving…' : rows.length > 1 ? `Save ${rows.length} POs` : 'Save PO'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
